/**
 * Bella cart promo codes — adapted from dcoded-cart-promo for #mini-cart / bella-cart-drawer.
 */
if (!customElements.get('dcoded-cart-promo')) {
  class DcodedCartPromo extends HTMLElement {
    static _delegationBound = false;

    static _initDelegation() {
      if (DcodedCartPromo._delegationBound) return;
      DcodedCartPromo._delegationBound = true;

      document.addEventListener(
        'click',
        (e) => {
          const target = e.target;
          if (!(target instanceof Element)) return;
          if (!target.closest('#mini-cart, mini-cart')) return;

          if (target.closest('[data-promo-close]')) {
            e.preventDefault();
            e.stopPropagation();
            DcodedCartPromo._closeAnyOpenSheet();
            return;
          }

          if (target.closest('[data-promo-overlay].dcoded-promo__overlay--visible')) {
            e.preventDefault();
            e.stopPropagation();
            DcodedCartPromo._closeAnyOpenSheet();
          }
        },
        true
      );

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') DcodedCartPromo._closeAnyOpenSheet();
      });
    }

    static _closeAnyOpenSheet() {
      document.querySelectorAll('[data-promo-sheet]').forEach((sheet) => {
        sheet.classList.remove('dcoded-promo__sheet--open');
        sheet.setAttribute('aria-hidden', 'true');
      });
      document.querySelectorAll('[data-promo-overlay]').forEach((overlay) => {
        overlay.classList.remove('dcoded-promo__overlay--visible');
        overlay.setAttribute('aria-hidden', 'true');
      });
      document.querySelector('#mini-cart')?.removeAttribute('data-promo-open');
    }

    static _isDrawerEmpty(root) {
      if (!(root instanceof Element)) return true;
      if (root.querySelector('.bella-cart.is-empty')) return true;
      const form = root.querySelector('form.bella-cart, form.mini-cart');
      if (form && form.classList.contains('is-empty')) return true;
      if (root.querySelector('.bella-cart__items li, .mini-cart__navigation li')) return false;
      return !root.querySelector('.bella-cart__footer, .mini-cart__footer');
    }

    static _clearPromoPortal(root) {
      if (!(root instanceof Element)) return;
      DcodedCartPromo._closeAnyOpenSheet();
      const promo = root.querySelector('dcoded-cart-promo');
      if (promo && typeof promo._restorePortalNodesToComponent === 'function') {
        promo._restorePortalNodesToComponent();
        promo._portalReady = false;
        return;
      }
      root.querySelector('[data-promo-portal]')?.replaceChildren();
    }

    connectedCallback() {
      DcodedCartPromo._initDelegation();

      this._sectionId = this.dataset.sectionId || 'bella-cart-drawer';
      this._portalReady = false;
      this._onCartChange = this._onCartChange.bind(this);
      this._cacheNodes();
      this._closeSheet();
      this._bindEvents();
      requestAnimationFrame(() => this._ensurePortalNodes());

      document.addEventListener('cart:updated', this._onCartChange);
      document.addEventListener('ajaxProduct:added', this._onCartChange);
      document.addEventListener('cartdrawer:opened', this._onCartChange);
      document.addEventListener('dcoded-cart-drawer-refreshed', this._onCartChange);

      const confettiWrap = this.querySelector('[data-promo-confetti]');
      if (this.dataset.appliedCode && confettiWrap) {
        this._burstConfetti(confettiWrap);
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this._onCartChange);
      document.removeEventListener('ajaxProduct:added', this._onCartChange);
      document.removeEventListener('cartdrawer:opened', this._onCartChange);
      document.removeEventListener('dcoded-cart-drawer-refreshed', this._onCartChange);
      this._abortController?.abort();
      this._abortController = null;
      DcodedCartPromo._closeAnyOpenSheet();
      this._restorePortalNodesToComponent();
      this._overlay = null;
      this._sheet = null;
      this._portalReady = false;
    }

    _onCartChange() {
      if (!this.isConnected) return;
      const root = this._getDrawerRoot();
      if (DcodedCartPromo._isDrawerEmpty(root)) {
        DcodedCartPromo._clearPromoPortal(root);
        this._portalReady = false;
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._reinitializeAfterCartChange());
      });
    }

    _restorePortalNodesToComponent() {
      const overlay = this._overlay || this.querySelector('[data-promo-overlay]');
      const sheet = this._sheet || this.querySelector('[data-promo-sheet]');
      if (overlay && overlay.parentElement !== this) {
        this.appendChild(overlay);
        this._overlay = overlay;
      }
      if (sheet && sheet.parentElement !== this) {
        this.appendChild(sheet);
        this._sheet = sheet;
      }
    }

    _reinitializeAfterCartChange() {
      if (!this.isConnected) return;
      const root = this._getDrawerRoot();
      if (DcodedCartPromo._isDrawerEmpty(root)) {
        DcodedCartPromo._clearPromoPortal(root);
        this._portalReady = false;
        return;
      }
      DcodedCartPromo._closeAnyOpenSheet();
      this._portalReady = false;
      this._overlay = null;
      this._sheet = null;
      this._restorePortalNodesToComponent();
      this._cacheNodes();
      this._ensurePortalNodes();
      this._bindEvents();
    }

    _cacheNodes() {
      const portalRoot = this._getPortalRoot();
      const portaledOverlay = portalRoot?.querySelector('[data-promo-overlay]');
      const portaledSheet = portalRoot?.querySelector('[data-promo-sheet]');
      const localOverlay = this.querySelector('[data-promo-overlay]');
      const localSheet = this.querySelector('[data-promo-sheet]');

      if (portaledOverlay) {
        if (localOverlay && localOverlay !== portaledOverlay) localOverlay.remove();
        this._overlay = portaledOverlay;
      } else {
        this._overlay = localOverlay || null;
      }

      if (portaledSheet) {
        if (localSheet && localSheet !== portaledSheet) localSheet.remove();
        this._sheet = portaledSheet;
      } else {
        this._sheet = localSheet || null;
      }
    }

    _bindEvents() {
      this._abortController?.abort();
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this.addEventListener(
        'click',
        (e) => {
          const target = e.target;
          if (!(target instanceof Element)) return;

          if (target.closest('[data-promo-remove]')) {
            e.preventDefault();
            e.stopPropagation();
            this._removeCode();
            return;
          }
          if (target.closest('[data-promo-change]')) {
            e.preventDefault();
            e.stopPropagation();
            this._openSheet();
            return;
          }
          if (target.closest('[data-promo-trigger]')) {
            e.preventDefault();
            e.stopPropagation();
            this._openSheet();
          }
        },
        { signal }
      );

      this._cacheNodes();
      if (!this._overlay || !this._sheet) return;

      this._overlay.addEventListener('click', () => this._closeSheet(), { signal });

      this._sheet.addEventListener(
        'click',
        (e) => {
          const target = e.target;
          if (!(target instanceof Element)) return;
          if (target.closest('[data-promo-close]')) {
            e.preventDefault();
            this._closeSheet();
            return;
          }
          if (target.closest('[data-promo-apply]')) {
            e.preventDefault();
            this._applyFromInput();
            return;
          }
          if (target.closest('[data-tap-code]')) {
            e.preventDefault();
            this._tapToUse(target.closest('[data-tap-code]'));
          }
        },
        { signal }
      );

      this._sheet.addEventListener(
        'keydown',
        (e) => {
          if (e.key !== 'Enter') return;
          if (!(e.target instanceof Element)) return;
          if (!e.target.matches('[data-promo-input], .dcoded-promo__input')) return;
          e.preventDefault();
          this._applyFromInput();
        },
        { signal }
      );
    }

    _getDrawerRoot() {
      return this.closest('#mini-cart') || document.getElementById('mini-cart') || this.closest('mini-cart');
    }

    _getPortalRoot() {
      const drawerRoot = this._getDrawerRoot();
      if (!drawerRoot) return null;

      let portal = drawerRoot.querySelector('[data-promo-portal]');
      if (!portal) {
        portal = document.createElement('div');
        portal.setAttribute('data-promo-portal', '');
        drawerRoot.appendChild(portal);
      }
      return portal;
    }

    _ensurePortalNodes() {
      this._cacheNodes();
      if (!this._overlay) this._overlay = this.querySelector('[data-promo-overlay]');
      if (!this._sheet) this._sheet = this.querySelector('[data-promo-sheet]');
      if (!this._overlay || !this._sheet) return;

      const portalRoot = this._getPortalRoot();
      if (!portalRoot) return;

      portalRoot.querySelectorAll('[data-promo-overlay]').forEach((node) => {
        if (node !== this._overlay) node.remove();
      });
      portalRoot.querySelectorAll('[data-promo-sheet]').forEach((node) => {
        if (node !== this._sheet) node.remove();
      });

      if (this._overlay.parentElement !== portalRoot) portalRoot.appendChild(this._overlay);
      if (this._sheet.parentElement !== portalRoot) portalRoot.appendChild(this._sheet);

      this._overlay.style.display = '';
      this._sheet.style.display = '';
      this._portalReady = true;
    }

    _closeSheet() {
      DcodedCartPromo._closeAnyOpenSheet();
    }

    _openSheet() {
      const drawerRoot = this._getDrawerRoot();
      if (DcodedCartPromo._isDrawerEmpty(drawerRoot)) {
        this._closeSheet();
        return;
      }

      this._restorePortalNodesToComponent();
      this._cacheNodes();
      if (!this._overlay) this._overlay = this.querySelector('[data-promo-overlay]');
      if (!this._sheet) this._sheet = this.querySelector('[data-promo-sheet]');
      this._ensurePortalNodes();
      if (!this._sheet || !this._overlay) return;

      const input = this._sheet.querySelector('.dcoded-promo__input');
      const error = this._sheet.querySelector('[data-promo-error]');

      if (drawerRoot) drawerRoot.setAttribute('data-promo-open', 'true');
      this._sheet.classList.add('dcoded-promo__sheet--open');
      this._overlay.classList.add('dcoded-promo__overlay--visible');
      this._sheet.setAttribute('aria-hidden', 'false');
      this._overlay.setAttribute('aria-hidden', 'false');

      if (input) {
        input.value = '';
        window.setTimeout(() => input.focus(), 350);
      }
      if (error) error.textContent = '';
    }

    _tapToUse(btn) {
      const code = btn.dataset.tapCode || '';
      if (!code) return;
      btn.classList.add('dcoded-promo__tap-btn--pulse', 'dcoded-promo__tap-btn--loading');
      btn.textContent = 'APPLYING...';
      this._applyCode(code);
    }

    _applyFromInput() {
      this._cacheNodes();
      const input = this._sheet?.querySelector('.dcoded-promo__input');
      const error = this._sheet?.querySelector('[data-promo-error]');
      if (!input) return;
      const code = input.value.trim();
      if (!code) return;
      if (error) error.textContent = '';
      this._applyCode(code);
    }

    _getCartUpdateUrl() {
      if (typeof theme !== 'undefined' && theme.routes && theme.routes.cart_update_url) {
        return theme.routes.cart_update_url;
      }
      return '/cart/update.js';
    }

    _getSectionIds() {
      const ids = [this._sectionId || 'bella-cart-drawer', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
      return [...new Set(ids.filter(Boolean))];
    }

    async _applyCode(code) {
      this._cacheNodes();
      const error = this._sheet?.querySelector('[data-promo-error]');
      try {
        const existingCodes = this._getExistingCodes();
        if (existingCodes.map((c) => c.toLowerCase()).includes(code.toLowerCase())) {
          if (error) error.textContent = 'This code is already applied.';
          this._resetTapButtons();
          return;
        }

        const allCodes = [...existingCodes, code].join(',');
        const res = await fetch(this._getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            discount: allCodes,
            sections: this._getSectionIds().join(','),
            sections_url: window.location.pathname,
          }),
        });

        const data = await res.json();

        if (
          data.discount_codes &&
          data.discount_codes.find((d) => d.code && d.code.toLowerCase() === code.toLowerCase() && d.applicable === false)
        ) {
          if (error) error.textContent = 'This code isn\u2019t valid for your cart.';
          this._resetTapButtons();
          return;
        }

        this._closeSheet();
        this.dataset.appliedCode = code;
        this._resetTapButtons();
        this._refreshCart(data);
      } catch (err) {
        if (error) error.textContent = 'Something went wrong. Please try again.';
        this._resetTapButtons();
      }
    }

    async _removeCode() {
      const currentCode = this.dataset.appliedCode;
      if (!currentCode) return;

      const existingCodes = this._getExistingCodes();
      const idx = existingCodes.findIndex((c) => c.toLowerCase() === currentCode.toLowerCase());
      if (idx !== -1) existingCodes.splice(idx, 1);

      try {
        const res = await fetch(this._getCartUpdateUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            discount: existingCodes.join(','),
            sections: this._getSectionIds().join(','),
            sections_url: window.location.pathname,
          }),
        });

        const data = await res.json();
        this.removeAttribute('data-applied-code');
        this._resetTapButtons();
        DcodedCartPromo._refreshCartSections(data);
      } catch (err) {
        console.error('[Bella promo] remove failed', err);
        location.reload();
      }
    }

    _getExistingCodes() {
      const codes = [];
      if (this.dataset.appliedCode) codes.push(this.dataset.appliedCode);
      document.querySelectorAll('[data-applied-code]').forEach((el) => {
        const code = el.getAttribute('data-applied-code');
        if (code && !codes.map((c) => c.toLowerCase()).includes(code.toLowerCase())) {
          codes.push(code);
        }
      });
      return codes;
    }

    _refreshCart(data) {
      DcodedCartPromo._refreshCartSections(data);
    }

    static _refreshCartSections(data) {
      const miniCart = document.querySelector('mini-cart');
      const sectionId =
        (miniCart && miniCart.dataset.sectionId) || 'bella-cart-drawer';

      let updated = false;

      const sectionHtml = data.sections && data.sections[sectionId];
      if (sectionHtml && miniCart) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionHtml, 'text/html');
        const shopifySection = doc.querySelector('.shopify-section');
        if (shopifySection) {
          miniCart.innerHTML = shopifySection.innerHTML;
          updated = true;
        }
      }

      ['cart-icon-bubble', 'mobile-cart-icon-bubble'].forEach((id) => {
        const html = data.sections && data.sections[id];
        const el = document.getElementById(id);
        if (!html || !el) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('.shopify-section');
        if (section) el.innerHTML = section.innerHTML;
      });

      document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
      document.dispatchEvent(new CustomEvent('dcoded-cart-drawer-refreshed'));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'bella-promo' } }));

      if (!updated) {
        const url = (miniCart && miniCart.dataset.url) || `${window.location.origin}/?section_id=${sectionId}`;
        fetch(url)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const section = doc.querySelector('.shopify-section');
            if (section && miniCart) {
              miniCart.innerHTML = section.innerHTML;
              document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
            }
          })
          .catch(() => location.reload());
      }
    }

    _resetTapButtons() {
      this._cacheNodes();
      const appliedCode = (this.dataset.appliedCode || '').toLowerCase();
      this._sheet?.querySelectorAll('[data-tap-code]').forEach((btn) => {
        const btnCode = (btn.dataset.tapCode || '').toLowerCase();
        btn.classList.remove(
          'dcoded-promo__tap-btn--pulse',
          'dcoded-promo__tap-btn--loading',
          'dcoded-promo__tap-btn--applied'
        );
        if (appliedCode && btnCode === appliedCode) {
          btn.textContent = 'APPLIED';
          btn.classList.add('dcoded-promo__tap-btn--applied');
        } else {
          btn.textContent = 'TAP TO USE';
        }
      });
    }

    _burstConfetti(wrap) {
      const colors = [
        'var(--bella-tan)',
        'var(--bella-rose)',
        'var(--bella-peach)',
        '#c89a89',
        '#a8755f',
        '#f4e6df',
        '#e8d5c8',
      ];
      const shapes = ['circle', 'rect'];

      for (let i = 0; i < 28; i++) {
        const piece = document.createElement('span');
        piece.classList.add('confetti-piece');
        const shape = shapes[Math.floor(Math.random() * shapes.length)];
        const color = colors[Math.floor(Math.random() * colors.length)];
        const left = Math.random() * 100;
        const delay = Math.random() * 0.5;
        const rotate = (Math.random() - 0.5) * 360;
        const size = 4 + Math.random() * 5;

        piece.style.left = left + '%';
        piece.style.animationDelay = delay + 's';
        piece.style.setProperty('--confetti-rotate', rotate + 'deg');

        if (shape === 'circle') {
          piece.style.width = size + 'px';
          piece.style.height = size + 'px';
          piece.style.borderRadius = '50%';
          piece.style.background = color;
        } else {
          piece.style.width = size * 0.6 + 'px';
          piece.style.height = size + 'px';
          piece.style.background = color;
          piece.style.borderRadius = '1px';
        }
        wrap.appendChild(piece);
      }

      setTimeout(() => {
        wrap.querySelectorAll('.confetti-piece').forEach((p) => p.remove());
      }, 2500);
    }
  }

  customElements.define('dcoded-cart-promo', DcodedCartPromo);
}
